// src/pages/InstitutionManagementPage.tsx

import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Container, Button, Input, TextArea } from '../styles/GlobalStyles';
import { addInstitution, getInstitutions, updateInstitution } from '../services/firebase';
import { Institution } from '../types';
import { useTranslation } from '../i18n';
import { useFormatDate } from '../hooks/useFormatDate';

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 40px;
  padding: 20px 0;
  border-bottom: 2px solid #e5e7eb;
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #1f2937;
`;

const SecretBadge = styled.div`
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const AddForm = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  margin-bottom: 32px;
`;

const FormTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 20px;
`;

const FormRow = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 20px;
  flex-wrap: wrap;
`;

const FormColumn = styled.div`
  flex: 1;
  min-width: 200px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #374151;
  font-size: 14px;
`;

const InstitutionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 20px;
`;

const InstitutionCard = styled.div<{ isActive: boolean }>`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 2px solid ${props => props.isActive ? '#15803d' : '#ef4444'};
  transition: all 0.2s ease;
  
  &:hover {
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
`;

const InstitutionNumber = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #1f2937;
`;

const StatusBadge = styled.div<{ isActive: boolean }>`
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  
  ${props => props.isActive 
    ? `
      background-color: #dcfce7;
      color: #166534;
    `
    : `
      background-color: #fecaca;
      color: #991b1b;
    `
  }
`;

const InstitutionName = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 8px;
`;

const InstitutionInfo = styled.div`
  font-size: 14px;
  color: #6b7280;
  line-height: 1.5;
  margin-bottom: 16px;
`;

const CardActions = styled.div`
  display: flex;
  gap: 8px;
`;

const SmallButton = styled.button<{ variant?: 'danger' | 'success' }>`
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid;
  transition: all 0.2s ease;
  
  ${props => props.variant === 'danger' 
    ? `
      background-color: #fef2f2;
      color: #dc2626;
      border-color: #fecaca;
      &:hover { 
        background-color: #dc2626; 
        color: white; 
      }
    `
    : props.variant === 'success'
    ? `
      background-color: #f0fdf4;
      color: #166534;
      border-color: #bbf7d0;
      &:hover { 
        background-color: #166534; 
        color: white; 
      }
    `
    : `
      background-color: #f8fafc;
      color: #475569;
      border-color: #e2e8f0;
      &:hover { 
        background-color: #475569; 
        color: white; 
      }
    `
  }
`;

const ErrorMessage = styled.div`
  color: #ef4444;
  font-size: 14px;
  margin-top: 8px;
  margin-bottom: 16px;
  padding: 8px 12px;
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
`;

const SuccessMessage = styled.div`
  color: #166534;
  font-size: 14px;
  margin-top: 8px;
  margin-bottom: 16px;
  padding: 8px 12px;
  background-color: #ecfdf5;
  border: 1px solid #a7f3d0;
  border-radius: 8px;
`;

const LoadingContainer = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #6b7280;
`;

const EmptyStateContainer = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #6b7280;
  background: white;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
`;

const InstitutionManagementPage: React.FC = () => {
  const { t } = useTranslation();
  const { formatDate } = useFormatDate();
  const [institutions, setInstitutions] = useState<Institution[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    institutionNumber: '',
    name: '',
    address: '',
    phone: ''
  });

  useEffect(() => {
    loadInstitutions();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadInstitutions = async () => {
    setLoading(true);
    try {
      const data = await getInstitutions();
      setInstitutions(data);
    } catch (error) {
      console.error('기관 목록 로드 오류:', error);
      setError(t('institution.errors.load'));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.institutionNumber.trim()) {
      setError(t('institution.errors.numberRequired'));
      return;
    }
    if (!formData.name.trim()) {
      setError(t('institution.errors.nameRequired'));
      return;
    }

    setIsSubmitting(true);
    setError('');
    setSuccess('');

    try {
      // 🔥 undefined 값을 전송하지 않도록 데이터 정리
      const institutionData: any = {
        institutionNumber: formData.institutionNumber.trim(),
        name: formData.name.trim(),
        isActive: false
      };

      // 선택적 필드들은 값이 있을 때만 추가
      if (formData.address && formData.address.trim()) {
        institutionData.address = formData.address.trim();
      }
      
      if (formData.phone && formData.phone.trim()) {
        institutionData.phone = formData.phone.trim();
      }

      const result = await addInstitution(institutionData);

      setSuccess(t('institution.success.add', { number: result.institutionNumber }));
      setFormData({ institutionNumber: '', name: '', address: '', phone: '' });
      await loadInstitutions();
    } catch (error) {
      console.error('기관 추가 오류:', error);
      setError(t('institution.errors.add'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleStatus = async (institution: Institution) => {
    try {
      // 🔥 업데이트할 때도 undefined 값 방지
      const updateData: any = { 
        isActive: !institution.isActive 
      };

      await updateInstitution(institution.id, updateData);
      const status = t(!institution.isActive ? 'common.active' : 'common.inactive');
      setSuccess(t('institution.success.update', { name: institution.name, status }));
      await loadInstitutions();
    } catch (error) {
      console.error('기관 상태 변경 오류:', error);
      setError(t('institution.errors.update'));
    }
  };

  if (loading) {
    return (
      <Container>
        <LoadingContainer>
          <div>{t('institution.loading')}</div>
        </LoadingContainer>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>{t('institution.title')}</Title>
        <SecretBadge>{t('institution.adminBadge')}</SecretBadge>
      </Header>

      <AddForm>
        <FormTitle>{t('institution.addForm.title')}</FormTitle>
        
        <form onSubmit={handleSubmit}>
          <FormRow>
            <FormColumn>
              <Label htmlFor="institutionNumber">{t('institution.fields.number')}</Label>
              <Input
                id="institutionNumber"
                name="institutionNumber"
                type="text"
                value={formData.institutionNumber}
                onChange={handleInputChange}
                placeholder={t('institution.placeholders.number')}
                disabled={isSubmitting}
                required
              />
            </FormColumn>
            <FormColumn>
              <Label htmlFor="name">{t('institution.fields.name')}</Label>
              <Input
                id="name"
                name="name"
                type="text"
                value={formData.name}
                onChange={handleInputChange}
                placeholder={t('institution.placeholders.name')}
                disabled={isSubmitting}
                required
              />
            </FormColumn>
            <FormColumn>
              <Label htmlFor="phone">{t('institution.fields.phone')}</Label>
              <Input
                id="phone"
                name="phone"
                type="text"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder={t('institution.placeholders.phone')}
                disabled={isSubmitting}
              />
            </FormColumn>
          </FormRow>

          <Label htmlFor="address">{t('institution.fields.address')}</Label>
          <TextArea
            id="address"
            name="address"
            value={formData.address}
            onChange={handleInputChange}
            placeholder={t('institution.placeholders.address')}
            disabled={isSubmitting}
          />

          {error && <ErrorMessage>{error}</ErrorMessage>}
          {success && <SuccessMessage>{success}</SuccessMessage>}

          <Button type="submit" variant="primary" disabled={isSubmitting}>
            {isSubmitting ? t('common.adding') : t('common.add')}
          </Button>
        </form>
      </AddForm>

      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#1f2937' }}>
          {t('institution.listTitle', { count: institutions.length })}
        </h2>
      </div>

      {institutions.length === 0 ? (
        <EmptyStateContainer>
          <div style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
            {t('institution.empty.title')}
          </div>
          <div style={{ fontSize: '14px' }}>
            {t('institution.empty.desc')}
          </div>
        </EmptyStateContainer>
      ) : (
        <InstitutionGrid>
          {institutions.map(institution => (
            <InstitutionCard key={institution.id} isActive={institution.isActive}>
              <CardHeader>
                <InstitutionNumber>{institution.institutionNumber}</InstitutionNumber>
                <StatusBadge isActive={institution.isActive}>
                  {institution.isActive ? t('common.active') : t('common.inactive')}
                </StatusBadge>
              </CardHeader>
              <InstitutionName>{institution.name}</InstitutionName>
              <InstitutionInfo>
                {/* 🔥 undefined 값 체크하여 안전하게 표시 */}
                {institution.address && institution.address.trim() && (
                  <div>{institution.address}</div>
                )}
                {institution.phone && institution.phone.trim() && (
                  <div>{institution.phone}</div>
                )}
                <div style={{ marginTop: '8px', fontSize: '12px' }}>
                  {t('institution.createdAt')}: {formatDate(institution.createdAt)}
                </div>
              </InstitutionInfo>
              <CardActions>
                <SmallButton
                  variant={institution.isActive ? 'danger' : 'success'}
                  onClick={() => handleToggleStatus(institution)}
                >
                  {institution.isActive ? t('institution.deactivate') : t('institution.activate')}
                </SmallButton>
              </CardActions>
            </InstitutionCard>
          ))}
        </InstitutionGrid>
      )}
    </Container>
  );
};

export default InstitutionManagementPage;